import { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FormLayout, Label } from '@/components/Forms';
import CurrencyField from '@/components/Forms/CurrencyField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import useAlerts from '@/hooks/useAlerts';
import { selectTeamTestingProperties } from '@/selectors';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { Dispatch, RootState } from '@/utils/store';

import PasteFileNameField from './PasteFileNameField';
import TestingChannelsSelectionField from './TestingChannelsSelectionField';
import ToggableCardHeading, { Box } from './ToggableCardHeading';

type FormDataT = {
  tips_max_cpv: string;
  clips_youtube_channels?: number[];
  clips_campaign_budget: string;
  clips_paste_file_name: boolean;
};

const InputsBox = styled.div`
  padding: 24px;
  border-top: solid 1px ${({ theme }) => theme.colors.grayscaleLight};
`;

const YoutubeClipsProperties = () => {
  const dispatch = useDispatch<Dispatch>();

  const [showContent, setShowContent] = useState(false);

  const { teamId } = useParams();

  const alerts = useAlerts();

  const { testingSettings, testingChannels } = useSelector((state: RootState) =>
    selectTeamTestingProperties(state, teamId!),
  );

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      tips_max_cpv: testingSettings?.tips_max_cpv?.toString() ?? '',
      clips_campaign_budget:
        testingSettings?.clips_campaign_budget?.toString() ?? '',
      clips_youtube_channels:
        testingChannels?.filter((c) => c.type === 'clips')?.map((c) => c.id) ??
        [],
      clips_paste_file_name: testingSettings?.clips_paste_file_name ?? false,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const properties = await api.post(
          `/v1/teams/${teamId}/testing-settings`,
          {
            ...data,
            network: 'youtube',
            type: 'clips',
          },
        );

        dispatch.teams.updateTeamTestingProperties({
          properties,
          teamId: parseInt(teamId!),
        });

        alerts.success('Youtube Clips properties and channels saved');
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Box>
      <form onSubmit={onSubmit}>
        <ToggableCardHeading
          setShowContent={setShowContent}
          count={
            testingChannels?.filter((c) => c.type === 'clips')?.map((c) => c.id)
              .length
          }
          showContent={showContent}
          title="Clips Properties & channels"
          hasSubmitButton
          isSubmitting={isSubmitting}
          isDirty={isDirty}
        />
        <InputsBox>
          <FormLayout>
            <PasteFileNameField<FormDataT>
              control={control}
              name="clips_paste_file_name"
            />
            <div>
              <Label htmlFor="clipsMaxCpv">Clips maximum CPV</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="tips_max_cpv"
                inputId="clipsMaxCpv"
                placeholder="Add amount"
              />
              {!!errors.tips_max_cpv && (
                <ValidationMessage>
                  {errors.tips_max_cpv.message}
                </ValidationMessage>
              )}
            </div>
            <div>
              <Label htmlFor="clipsBudget">Clips campaign budget</Label>
              <CurrencyField<FormDataT>
                control={control}
                name="clips_campaign_budget"
                inputId="clipsBudget"
                placeholder="Add amount"
              />
              {!!errors.clips_campaign_budget && (
                <ValidationMessage>
                  {errors.clips_campaign_budget.message}
                </ValidationMessage>
              )}
            </div>
          </FormLayout>
        </InputsBox>
        {!!showContent && (
          <TestingChannelsSelectionField<FormDataT>
            control={control}
            name="clips_youtube_channels"
            label="Channels Options"
          />
        )}
      </form>
    </Box>
  );
};

export default YoutubeClipsProperties;
